import * as React from "react";
import {
  Box,
  Paper,
  Typography,
  Divider,
  Button,
  List,
  ListItem,
  IconButton,
  CircularProgress,
  Chip,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DescriptionIcon from "@mui/icons-material/Description";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

// import { readMultipleXmlFiles } from "../../utilities/Utilities.js";
import { saveNfeXml } from "../../services/uploadXmlService.js";

function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;

    reader.readAsText(file);
  });
}

const UploadXml = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [files, setFiles] = React.useState([]);
  const [dragActive, setDragActive] = React.useState(false);

  const inputRef = React.useRef(null);

  const [xmlData, setXmlData] = React.useState([]);
  const [error, setError] = React.useState("");

const addFiles = async (fileList) => {
  setError("");

  const selectedFiles = Array.from(fileList || []).filter((file) =>
    file.name.toLowerCase().endsWith(".xml"),
  );

  if (selectedFiles.length === 0) {
    setError(t("message.msg0016"));
    return;
  }

  setFiles((prev) => {
    const existingNames = new Set(prev.map((f) => `${f.name}-${f.size}`));
    const newFiles = selectedFiles.filter(
      (f) => !existingNames.has(`${f.name}-${f.size}`),
    );
    return [...prev, ...newFiles];
  });

  setXmlData((prev) => {
    const existingNames = new Set(
      prev.map((f) => `${f.fileName}-${f.fileSize}`),
    );

    const newItems = selectedFiles
      .filter((f) => !existingNames.has(`${f.name}-${f.size}`))
      .map((f) => ({
        fileName: f.name,
        fileSize: f.size,
        status: "",
        backendMessage: "",
        technicalMessage: "",
      }));

    return [...prev, ...newItems];
  });
};

  const handleInputChange = (event) => {
    addFiles(event.target.files);
    event.target.value = "";
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
    addFiles(event.dataTransfer.files);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
  };

  const handleRemoveFile = (fileToRemove) => {
    setFiles((prev) =>
      prev.filter(
        (file) =>
          !(file.name === fileToRemove.name && file.size === fileToRemove.size),
      ),
    );

    setXmlData((prev) =>
      prev.filter(
        (file) =>
          !(
            file.fileName === fileToRemove.name &&
            file.fileSize === fileToRemove.size
          ),
      ),
    );
  };


const handleUpload = async () => {
  if (files.length === 0) {
    setError(t("message.msg0018"));
    return;
  }

  setXmlData((prev) =>
    prev.map((xml) => ({
      ...xml,
      status: "loading",
      backendMessage: "",
      technicalMessage: "",
    })),
  );

  try {
    const documents = await Promise.all(
      files.map(async (file) => {
        const xmlContent = await readFileAsText(file);

        return {
          fileName: file.name,
          fileSize: file.size,
          xmlContent,
        };
      }),
    );

    const response = await saveNfeXml({ documents });

    setXmlData((prev) =>
      prev.map((xml) => {
        const resultItem = response.results?.find(
          (r) => r.fileName === xml.fileName,
        );

        return {
          ...xml,
          status: resultItem?.status || "error",
          backendMessage: resultItem?.message || t("uploadXml.errorProcess"),
          technicalMessage: resultItem?.technicalMessage || "",
        };
      }),
    );
  } catch (err) {
    console.error(err);

    setXmlData((prev) =>
      prev.map((xml) => ({
        ...xml,
        status: "error",
        backendMessage:
          err?.response?.data?.error ||
          err?.response?.data?.details ||
          err?.message ||
          t("uploadXml.errorUpload"),
      })),
    );
  }
};

  const isUploading = xmlData.some((x) => x.status === "loading");

  return (
    <Box sx={{ p: 2 }}>
      <Typography
        variant="h5"
        sx={{ fontWeight: 800, color: "text.primary", letterSpacing: "-0.02em", mb: 3 }}
      >
        {t("uploadXml.pageTitle")}
      </Typography>

      <Paper
        elevation={0}
        sx={{ border: "1px solid", borderColor: "divider", borderRadius: "16px", overflow: "hidden", bgcolor: "background.paper" }}
      >
        <Box sx={{ p: 3, borderBottom: "1px solid", borderColor: "divider" }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "text.primary" }}>
            {t("uploadXml.title")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t("uploadXml.description")}
          </Typography>
        </Box>

        <Box sx={{ p: 3 }}>
          <Paper
            variant="outlined"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            sx={{
              p: 6,
              textAlign: "center",
              borderStyle: "dashed",
              borderWidth: 2,
              borderRadius: "12px",
              borderColor: dragActive ? "primary.main" : "divider",
              backgroundColor: dragActive ? "rgba(37, 99, 235, 0.04)" : "background.subtle",
              transition: "all 0.2s ease",
              cursor: "pointer",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              "&:hover": { borderColor: "primary.main", bgcolor: "rgba(37, 99, 235, 0.02)" },
            }}
            onClick={() => inputRef.current?.click()}
          >
            <Box
              sx={{
                bgcolor: dragActive ? "primary.main" : "text.secondary",
                color: "#fff",
                borderRadius: "50%",
                p: 1.5,
                mb: 2,
                transition: "0.2s",
                display: "flex",
              }}
            >
              <CloudUploadIcon sx={{ fontSize: 40 }} />
            </Box>

            <Typography variant="h6" sx={{ fontWeight: 800, color: "text.primary", mb: 1 }}>
              {t("uploadXml.dragTitle")}
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t("uploadXml.dragSubtitle")}
            </Typography>

            <Button
              variant="contained"
              startIcon={<UploadFileIcon />}
              onClick={(e) => {
                e.stopPropagation();
                inputRef.current?.click();
              }}
              sx={{ px: 4, py: 1, borderRadius: "8px", textTransform: "none" }}
            >
              {t("uploadXml.selectFiles")}
            </Button>

            <input
              ref={inputRef}
              type="file"
              accept=".xml,text/xml,application/xml"
              multiple
              hidden
              onChange={handleInputChange}
            />
          </Paper>
        </Box>

        {error && (
          <Box
            sx={{
              mx: 3,
              mb: 3,
              p: 2,
              bgcolor: "#fee2e2",
              borderRadius: "8px",
              border: "1px solid #fca5a5",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <ErrorOutlineIcon color="error" fontSize="small" />
            <Typography color="error.main" variant="body2" fontWeight={600}>
              {error}
            </Typography>
          </Box>
        )}

        <Divider />

        <Box sx={{ p: 3, bgcolor: "background.subtle" }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "text.primary", mb: 2 }}>
            {t("uploadXml.selectedFiles")}
          </Typography>

          {files.length === 0 ? (
            <Paper
              variant="outlined"
              sx={{
                p: 3,
                backgroundColor: "background.paper",
                borderStyle: "dashed",
                borderColor: "divider",
                textAlign: "center",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {t("uploadXml.empty")}
              </Typography>
            </Paper>
          ) : (
            <List dense sx={{ p: 0, display: "flex", flexDirection: "column", gap: 1 }}>
              {files.map((file, index) => {
                const xmlInfo =
                  xmlData.find(
                    (x) => x.fileName === file.name && x.fileSize === file.size,
                  ) || {};

                const isLoading = xmlInfo.status === "loading";
                const isSaved = xmlInfo.status === "saved";
                const isDuplicated = xmlInfo.status === "duplicated";
                const isError = xmlInfo.status === "error";

                return (
                  <ListItem
                    key={`${file.name}-${file.size}-${index}`}
                    component={Paper}
                    elevation={0}
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: "8px",
                      p: 1.5,
                      bgcolor: "background.paper",
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    <Box sx={{ p: 1, bgcolor: "#eff6ff", borderRadius: "8px", color: "primary.main", display: "flex" }}>
                      <DescriptionIcon />
                    </Box>

                    <Box sx={{ flex: 1, overflow: "hidden" }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 700, color: "text.primary", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                      >
                        {file.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>
                        {(file.size / 1024).toFixed(1)} KB
                      </Typography>
                      {xmlInfo.backendMessage && (
                        <Typography
                          variant="caption"
                          sx={{ display: "block", color: isError ? "#ef4444" : "text.secondary", mt: 0.3 }}
                        >
                          {xmlInfo.backendMessage}
                        </Typography>
                      )}
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: { xs: "auto", sm: 200 }, justifyContent: "flex-end" }}>
                      {isLoading && <CircularProgress size={20} />}

                      {isSaved && (
                        <Chip
                          icon={<CheckCircleIcon />}
                          label={t("status.saved")}
                          color="success"
                          size="small"
                          variant="outlined"
                          sx={{ fontWeight: 700 }}
                        />
                      )}
                      {isDuplicated && (
                        <Chip
                          label={t("status.duplicated")}
                          color="warning"
                          size="small"
                          variant="outlined"
                          sx={{ fontWeight: 700 }}
                        />
                      )}
                      {isError && (
                        <Chip
                          icon={<ErrorOutlineIcon />}
                          label={t("status.error")}
                          color="error"
                          size="small"
                          variant="outlined"
                          sx={{ fontWeight: 700 }}
                        />
                      )}

                      {!isLoading && !isSaved && !isDuplicated && !isError && (
                        <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, fontStyle: "italic" }}>
                          {t("status.pending")}
                        </Typography>
                      )}

                      <IconButton
                        edge="end"
                        onClick={() => handleRemoveFile(file)}
                        disabled={isLoading}
                        sx={{ color: "text.secondary", "&:hover": { color: "#ef4444", bgcolor: "#fee2e2" } }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </ListItem>
                );
              })}
            </List>
          )}
        </Box>

        <Box sx={{ p: 2, borderTop: "1px solid", borderColor: "divider", bgcolor: "background.paper", display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => navigate(-1)}
            sx={{ border: "1px solid", borderColor: "divider", px: 4, textTransform: "none" }}
          >
            {t("buttons.bClose")}
          </Button>

          <Button
            variant="contained"
            startIcon={isUploading ? <CircularProgress size={16} color="inherit" /> : <UploadFileIcon />}
            onClick={handleUpload}
            disabled={files.length === 0 || isUploading}
            sx={{ px: 4, textTransform: "none" }}
          >
            {isUploading ? `${t("status.loading")}...` : t("buttons.uploadSend")}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default UploadXml;